import { createContext, useCallback, useContext, useMemo, useRef } from "react";

import type { CommunityFeedItemV2Dto } from "@/api/types";
import { useInterval } from "@/hooks/useInterval";
import { communityFeedMutations } from "@/queries/community-feed/mutations";

type CommunityPostViewCountTrackingContentValues = {
  addToQueue: ({ itemId, type }: { itemId: string; type: CommunityFeedItemV2Dto["type"] }) => void;
};

const CommunityPostViewCountTrackingContext = createContext<CommunityPostViewCountTrackingContentValues | undefined>(
  undefined,
);

export const useCommunityPostViewCountTrackingManager = (): CommunityPostViewCountTrackingContentValues => {
  const context = useContext(CommunityPostViewCountTrackingContext);

  if (!context) {
    throw new Error(
      "useCommunityPostViewCountTrackingManager must be used within a CommunityPostViewCountTrackingProvider",
    );
  }

  return context;
};

const createDefaultQueue = () => ({
  posts: new Set<string>(),
  polls: new Set<string>(),
  surveys: new Set<string>(),
});

export function CommunityPostViewCountTrackingProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const defaultQueue = useMemo(() => createDefaultQueue(), []);
  const queue = useRef(defaultQueue);

  const addViewCountMutation = communityFeedMutations.useAddViewCount();

  useInterval(() => {
    if (queue.current.posts.size || queue.current.polls.size || queue.current.surveys.size) {
      addViewCountMutation.mutate({
        data: {
          viewedPosts: Array.from(queue.current.posts),
          viewedPolls: Array.from(queue.current.polls),
          viewedSurveys: Array.from(queue.current.surveys),
        },
      });
      queue.current = createDefaultQueue();
    }
  }, 1000);

  const addToQueue = useCallback(({ itemId, type }: { itemId: string; type: CommunityFeedItemV2Dto["type"] }) => {
    switch (type) {
      case "message":
        queue.current.posts.add(itemId);

        break;

      case "poll":
        queue.current.polls.add(itemId);
        break;

      case "survey":
        queue.current.surveys.add(itemId);
        break;

      default:
        console.warn(`Unknown item type: ${type}`);
        break;
    }
  }, []);

  return (
    <CommunityPostViewCountTrackingContext
      value={useMemo(
        () => ({
          addToQueue,
        }),
        [addToQueue],
      )}
    >
      {children}
    </CommunityPostViewCountTrackingContext>
  );
}
